<template>
  <v-container class="pa-0">
    <v-row v-if="applications.length > 5 && !fetchApplicationsPending">
      <v-col :cols="12" :sm="4">
        <v-autocomplete
          v-if="jobs.length > 1"
          :items="jobsSelect"
          v-model="selectedJob"
          :label="$t('texts.job')"
          item-text="title"
          item-value="id"
          hide-details
        />
      </v-col>
      <v-col :cols="12" :sm="4" :offset-sm="4">
        <v-text-field
          append-icon="search"
          :label="$t('texts.search')"
          single-line
          hide-details
          v-model="search"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col :cols="12">
        <v-data-table
          :headers="headers"
          :items="formattedItems"
          :search="search"
          :loading="initialLoadingPending || fetchApplicationsPending"
          must-sort
          :no-data-text="$t('texts.activeApplicationsTablePlaceholder')"
          :no-results-text="$t('texts.noActiveApplications')"
          :footer-props="{ itemsPerPageText: $t('texts.rowsPerPage')}"
          :hide-default-footer="applications.length < 5 || fetchApplicationsPending"
          class="elevation-0"
        >
          <template v-slot:item.actions="{ item }">
            <v-tooltip color="info" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon class="mx-0"
                  @click="showPdfDialog({
                    id: item.id,
                    status: item.status,
                    title: `${item.job} - ${item.formattedName}`,
                    pdfLink: item.application_pdf
                  })">
                  <v-icon color="primary">insert_drive_file</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('texts.showApplication') }}</span>
            </v-tooltip>
            <v-tooltip color="info" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon class="mx-0"
                  @click="showEditApplicationDialog(item)"
                >
                  <v-icon color="primary">edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('texts.edit') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapCompanyJobsState } = createNamespacedHelpers('company/jobs');
const { mapState: mapCompanyApplicationsState, mapActions: mapCompanyApplicationsActions } = createNamespacedHelpers('company/applications');

export default {
  name: 'CompanyApplicationOngoing',
  data() {
    return {
      search: '',
      selectedJob: 0,
    };
  },
  created() {
    this.initialLoading({
      companyId: this.selectedCompanyId,
      listType: 'open',
    });
  },
  computed: {
    ...mapCompanyJobsState([
      'jobs',
    ]),
    ...mapCompanyApplicationsState([
      'initialLoadingPending',
      'fetchApplicationsPending',
      'applications',
      'updateApplicationPending',
    ]),
    headers() {
      const headers = [
        { text: this.$i18n.t('texts.applicants'), value: 'formattedName', align: 'left' },
        { text: this.$i18n.t('texts.workplace'), value: 'job', align: 'left' },
        { text: this.$i18n.t('texts.startOfWork'), value: 'formattedStartMonth', align: 'left' },
        { text: this.$i18n.t('texts.endOfWork'), value: 'formattedEndMonth', align: 'left' },
        { text: this.$i18n.t('texts.appliedForOn'), value: 'formattedApplicationSent', align: 'left' },
        { text: this.$i18n.t('texts.status'), value: 'formattedStatus', align: 'left' },
        { text: '', value: 'actions' },
      ];
      return headers;
    },
    formattedItems() {
      if (this.applications.length > 0) {
        return this.applications.map((item) => {
          const formattedName = `${item.employee_first_name} ${item.employee_last_name}`;
          const startMonth = moment([item.start_year, item.start_month - 1]);
          const formattedStartMonth = this.$options.filters.formatDateMonth(startMonth);
          const endMonth = moment([item.end_year, item.end_month - 1]);
          const formattedEndMonth = this.$options.filters.formatDateMonth(endMonth);
          const formattedApplicationSent = this.$options.filters.formatDateShort(item.application_sent);
          const formattedStatus = this.$i18n.t(`texts.applicationStatus.${item.status}`);
          const formattedItem = {
            ...item,
            formattedName,
            formattedStartMonth,
            formattedEndMonth,
            formattedApplicationSent,
            formattedStatus,
          };
          return formattedItem;
        });
      }
      return [];
    },
    jobsSelect() {
      const all = {
        count: 0, id: 0, title: 'Alle',
      };
      return [all, ...this.jobs];
    },
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
  },
  methods: {
    ...mapCompanyApplicationsActions([
      'initialLoading',
      'fetchApplications',
      'updateApplication',
    ]),
    showEditApplicationDialog(item) {
      this.$eventBus.$emit('show-edit-application-dialog', item);
    },
    showPdfDialog(item) {
      if (item.status === 'SENT') {
        const payload = {
          id: item.id,
          companyId: this.selectedCompanyId,
          values: {
            status: 'READ',
          },
        };
        this.updateApplication(payload);
        this.$eventBus.$emit('show-pdf-dialog', item);
      } else {
        this.$eventBus.$emit('show-pdf-dialog', item);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  ::v-deep .status-SENT td {
    font-weight: 700;
  }
</style>
